import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef} from "react";
import {Column, Group} from "./Table";
import {TablePersonalizeContext} from "./TablePersonalize";
import {useSyncedRef} from "../SyncedRef";

// personalization flag: fit a column to its content after each cell edit
export const autoSizeOnEditFlag = "autoSizeOnEdit";

type Measurer = {
    column: Column<any>;
    measure: () => number;
}

export const TableAutoSizeContext = createContext({
    autoSizeOnEdit: true,
    setAutoSizeOnEdit: (value: boolean) => {},
    // register adds the width measurer for a visible column and returns the un-register callback
    register: (column: Column<any>, measure: () => number) => (() => {}),
    // autoSize fits the named columns (all registered columns when null) to their content
    autoSize: (names: string[] | null) => {},
    // autoSizeAfterEdit fits the named columns once the data that follows the edit has rendered
    autoSizeAfterEdit: (names: string[]) => {},
});

export function TableAutoSizeProvider(props: PropsWithChildren<{
    // the rendered table data; a change here flushes pending after-edit auto-sizes
    data: Group<any>[];
}>) {
    const personalize = useContext(TablePersonalizeContext);
    const personalizeRef = useSyncedRef(personalize);
    const measurers = useRef<{[name: string]: Measurer}>({});
    const pendingRef = useRef<string[]>([]);

    const autoSizeOnEdit = personalize.flags[autoSizeOnEditFlag] ?? true;
    const autoSizeOnEditRef = useSyncedRef(autoSizeOnEdit);
    const setFlag = personalize.setFlag;

    const setAutoSizeOnEdit = useCallback((value: boolean) => {
        setFlag(autoSizeOnEditFlag, value);
    }, [setFlag]);

    const register = useCallback((column: Column<any>, measure: () => number) => {
        const entry = {column, measure};
        measurers.current[column.name] = entry;

        return () => {
            if(measurers.current[column.name] === entry) {
                delete measurers.current[column.name];
            }
        }
    }, []);

    const autoSize = useCallback((names: string[] | null) => {
        const ctx = personalizeRef.current;
        const list = names === null ? Object.keys(measurers.current) : names;

        let byName = ctx.columns;
        let changed = false;

        list.map(name => {
            const entry = measurers.current[name];
            if(!entry) return null;

            const cfg = ctx.lookupByName(name, entry.column);
            const width = entry.measure();
            if(width === cfg.width) return null;

            byName = Object.assign({}, byName, {
                [name]: Object.assign({}, cfg, {width}),
            });
            changed = true;
            return null;
        });

        if(changed) {
            ctx.onChange({byName});
        }
    }, [personalizeRef]);

    const autoSizeAfterEdit = useCallback((names: string[]) => {
        if(!autoSizeOnEditRef.current) return;
        pendingRef.current = Array.from(new Set([...pendingRef.current, ...names]));
    }, [autoSizeOnEditRef]);

    // the reload after an edit replaces the table data; measure once that data has rendered
    useEffect(() => {
        if(pendingRef.current.length === 0) return;

        const names = pendingRef.current;
        pendingRef.current = [];
        autoSize(names);
    }, [props.data, autoSize]);

    const ctx = useMemo(() => ({
        autoSizeOnEdit,
        setAutoSizeOnEdit,
        register,
        autoSize,
        autoSizeAfterEdit,
    }), [autoSizeOnEdit, setAutoSizeOnEdit, register, autoSize, autoSizeAfterEdit]);

    return <TableAutoSizeContext.Provider value={ctx}>
        {props.children}
    </TableAutoSizeContext.Provider>
}
